import { useMemo, useState } from 'react';
import Icon from '../Icon';
import { OutlookChart, PriceMarketCharts, TrendBars, colors } from './charts';
import { bookedDateSet, centsToMoney, dollars, longDate, parseDay, percent, rangeLabel, shortDate, timeLabel } from './format';
import './pricing.css';

const PERIOD_LABELS = { year: 'Year', quarter: 'This quarter', lastQuarter: 'Last quarter', month: 'Month' };
const PERIOD_TITLES = { year: 'Year to date', quarter: 'Quarter to date', lastQuarter: 'Last quarter', month: 'Month to date' };

// ── Loading and error states ───────────────────────────────────────────────────────────────────────────────────────
function Unavailable({ state, error, what }) {
  const waiting = state === 'loading';
  return (
    <div className="pb-empty">
      <Icon name={waiting ? 'sync_alt' : 'cloud_off'} />
      <strong>{waiting ? `Loading ${what}…` : `${what} ${state === 'unavailable' ? 'is not set up yet' : 'is unavailable'}`}</strong>
      {!waiting && <small>{error || 'It will retry automatically.'}</small>}
    </div>
  );
}

function SegmentedControl({ options, value, onChange, label }) {
  return (
    <div className="pb-segmented" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button key={key} type="button" className={value === key ? 'active' : ''} aria-pressed={value === key} onClick={() => onChange(key)}>{text}</button>
      ))}
    </div>
  );
}

// ── Pricing sync strip ─────────────────────────────────────────────────────────────────────────────────────────────
export function SyncStrip({ pricing }) {
  if (!pricing) return null;
  const { sync } = pricing;
  return (
    <div className={`pb-sync ${sync.pushEnabled && !pricing.stale ? 'ok' : 'warn'}`}>
      <Icon name={sync.pushEnabled ? 'sync_alt' : 'warning'} />
      <span>PriceLabs {sync.pushEnabled ? 'syncing to Hospitable' : 'push is off'}</span>
      <span>Refreshed {timeLabel(sync.lastRefreshedAt)}</span>
      <span>Pushed {timeLabel(sync.lastPushedAt)}</span>
      {sync.openDays > 0 && <span>{sync.matchingDays} of {sync.openDays} open nights match Hospitable</span>}
      {pricing.stale && <span className="pb-stale">Showing last saved data</span>}
    </div>
  );
}

// ── KPI tiles ──────────────────────────────────────────────────────────────────────────────────────────────────────
const KPI_TILES = [
  { key: 'bookings', label: 'Bookings', blurb: 'Reservations with at least one night in this period.', value: (m) => m.bookings, format: (v) => String(v) },
  { key: 'averageStay', blurb: 'Average length of those reservations.', label: 'Avg stay', value: (m) => m.averageStay, format: (v) => String(v), unit: 'nights' },
  { key: 'bookedNights', blurb: 'Nights guests stayed in this period.', label: 'Booked nights', value: (m) => m.bookedNights, format: (v) => String(v) },
  { key: 'occupancy', blurb: 'Booked nights out of the nights you could sell.', label: 'Occupancy', value: (m) => m.occupancyPercent, format: (v) => percent(v, 1), diff: (v) => `${Math.abs(v).toFixed(1)} pts` },
  { key: 'adr', blurb: 'Average daily rate: room revenue per booked night.', label: 'ADR', value: (m) => m.adr, format: (v) => centsToMoney(v), diff: (v) => centsToMoney(Math.abs(v)) },
  { key: 'revPar', blurb: 'Room revenue per sellable night, booked or not.', label: 'RevPAR', value: (m) => m.revPar, format: (v) => centsToMoney(v), diff: (v) => centsToMoney(Math.abs(v)) },
  { key: 'window', blurb: 'Median days between booking and arrival.', label: 'Booking window', value: (m) => m.medianBookingWindow, format: (v) => String(v), unit: 'days', neutral: true },
  { key: 'blocked', blurb: 'Nights closed to guests: owner stays and calendar blocks.', label: 'Blocked nights', value: (m) => m.blockedNights, format: (v) => String(v), lowerIsBetter: true },
];

function Delta({ current, previous, tile }) {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return <em className="pb-delta none">&nbsp;</em>;
  const change = current - previous;
  const rounded = Math.round(change * 100) / 100;
  if (rounded === 0) return <em className="pb-delta flat">no change</em>;
  const good = tile.neutral ? null : tile.lowerIsBetter ? rounded < 0 : rounded > 0;
  const text = tile.diff ? tile.diff(rounded) : String(Math.abs(rounded));
  return (
    <em className={`pb-delta ${good === null ? 'neutral' : good ? 'good' : 'bad'}`}>
      <Icon name={rounded > 0 ? 'arrow_upward' : 'arrow_downward'} />{text}
    </em>
  );
}

function RevenueTile({ current, previous }) {
  const revenue = current.revenue;
  const lines = [
    ['Accommodation', revenue.accommodation],
    ['Cleaning & fees', revenue.fees],
    ['Discounts', revenue.discounts],
    ['Taxes', revenue.taxes],
    ['Adjustments', revenue.adjustments],
    ['Channel fees', revenue.channelFees],
  ].filter(([label, amount]) => amount !== 0 || label === 'Accommodation');
  return (
    <article className="pb-kpi pb-revenue">
      <span>Revenue</span>
      <strong>{centsToMoney(revenue.total)}</strong>
      <Delta current={revenue.total} previous={previous?.revenue.total} tile={{ diff: (v) => centsToMoney(Math.abs(v)) }} />
      <small className="pb-kpi-blurb">What you receive after channel fees and discounts.</small>
      <ul>
        {lines.map(([label, amount]) => (
          <li key={label} className={amount < 0 ? 'negative' : ''}><span>{label}</span><b>{centsToMoney(amount, { signed: label !== 'Accommodation' })}</b></li>
        ))}
        <li className="total"><span>Total payout</span><b>{centsToMoney(revenue.total)}</b></li>
      </ul>
    </article>
  );
}

export function KpiView({ kpis, state, error, period, compare, compact = false }) {
  if (!kpis) return <Unavailable state={state} error={error} what="KPIs" />;
  const data = kpis.periods[period];
  const baseline = compare === 'lastYear' ? data.lastYear : data.prior;
  const current = data.current;
  const previous = baseline.metrics;
  const compareName = compare === 'lastYear' || period === 'year' ? 'the same dates last year' : period === 'lastQuarter' ? 'the quarter before it' : `the same days of the previous ${period}`;
  return (
    <div className={`pb-kpis ${compact ? 'compact' : ''}`}>
      <div className="pb-kpi-heading">
        <strong>{PERIOD_TITLES[period]}</strong>
        <span>{rangeLabel(data.range)} · stay dates{previous ? ` · vs ${rangeLabel(baseline.range)}` : ''}</span>
      </div>
      {!current ? (
        <div className="pb-empty"><Icon name="info" /><strong>No bookings yet in this period</strong><small>Numbers appear once Hospitable has stays to report.</small></div>
      ) : (
        <>
          <div className="pb-kpi-grid">
            <RevenueTile current={current} previous={previous} />
            {KPI_TILES.map((tile) => {
              const value = tile.value(current);
              return (
                <article className="pb-kpi" key={tile.key}>
                  <span>{tile.label}</span>
                  <strong>{Number.isFinite(value) ? tile.format(value) : '—'}{Number.isFinite(value) && tile.unit && <small className="pb-unit"> {tile.unit}</small>}</strong>
                  <Delta current={value} previous={previous ? tile.value(previous) : null} tile={tile} />
                  <small className="pb-kpi-blurb">{tile.blurb}</small>
                </article>
              );
            })}
          </div>
          <p className="pb-kpi-note">
            {previous ? `Arrows compare with ${compareName}.` : `No bookings to compare with ${compareName} yet, so there are no arrows.`}
            {!compact && ` Occupancy, ADR and RevPAR use sellable nights since the first booking (${kpis.trackingStart ? shortDate(kpis.trackingStart) : '—'}).`}
          </p>
        </>
      )}
    </div>
  );
}

// ── Pace vs market ─────────────────────────────────────────────────────────────────────────────────────────────────
function PaceRow({ window }) {
  const own = window.occupancy;
  const market = window.marketOccupancy;
  const index = window.penetrationIndex;
  const tone = !Number.isFinite(index) ? 'flat' : index >= 1 ? 'good' : index >= 0.7 ? 'warn' : 'bad';
  return (
    <div className="pb-pace-row">
      <span className="pb-pace-label">Next {window.days} days</span>
      <div className="pb-pace-bars">
        <div><i style={{ width: `${Math.min(100, own ?? 0)}%`, background: colors.yours }} /><b>{percent(own)}</b><small>You</small></div>
        <div><i style={{ width: `${Math.min(100, market ?? 0)}%`, background: colors.market }} /><b>{percent(market)}</b><small>Market</small></div>
      </div>
      <span className={`pb-mpi ${tone}`}><b>{Number.isFinite(index) ? index.toFixed(2) : '—'}</b><small>market index</small></span>
    </div>
  );
}

export function PaceView({ pricing, state, error, compact = false }) {
  if (!pricing) return <Unavailable state={state} error={error} what="Pace data" />;
  const { pace } = pricing;
  const lead = pace.windows[0];
  const trailing = pace.windows.find((window) => window.penetrationIndex !== null && window.penetrationIndex < 0.8);
  return (
    <div className={`pb-pace ${compact ? 'compact' : ''}`}>
      <p className="pb-pace-headline">
        {lead.penetrationIndex >= 1 ? 'You lead the market for the coming week' : 'You trail the market for the coming week'}
        {trailing ? ` and fall behind from about ${trailing.days} days out.` : '.'}
        <small> A market index of 1.00 means your occupancy matches the market.</small>
      </p>
      <div className="pb-pace-rows">{pace.windows.map((window) => <PaceRow key={window.days} window={window} />)}</div>
      <div className="pb-pace-foot">
        <section>
          <h4>Booking pickup</h4>
          <div className="pb-pickup">
            {pace.pickup.slice(0, compact ? 4 : 5).map((item) => (
              <span key={item.days}><b>{item.bookings ?? '—'}</b><small>last {item.days}d{item.nights ? ` · ${item.nights} nights` : ''}</small></span>
            ))}
          </div>
        </section>
        <section>
          <h4>Next 30 days by day type</h4>
          <div className="pb-daytype">
            <span><small>Weekends</small><b>{percent(pace.weekend.own)}</b><em>market {percent(pace.weekend.market)}</em></span>
            <span><small>Weekdays</small><b>{percent(pace.weekday.own)}</b><em>market {percent(pace.weekday.market)}</em></span>
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Price vs market ────────────────────────────────────────────────────────────────────────────────────────────────
export function PriceView({ pricing, state, error, reservations, rangeDays, interactive, showPosition = false }) {
  const booked = useMemo(() => bookedDateSet(reservations), [reservations]);
  if (!pricing) return <Unavailable state={state} error={error} what="Pricing data" />;
  return <PriceMarketCharts pricing={pricing} bookedDates={booked} rangeDays={rangeDays} interactive={interactive} showPosition={showPosition} />;
}

// ── Market outlook ─────────────────────────────────────────────────────────────────────────────────────────────────
export function OutlookView({ pricing, state, error, reservations, rangeDays, interactive }) {
  const booked = useMemo(() => bookedDateSet(reservations), [reservations]);
  if (!pricing) return <Unavailable state={state} error={error} what="Market data" />;
  if (!pricing.marketOccupancy) return <Unavailable state="unavailable" what="Market outlook" />;
  const trends = pricing.marketTrends;
  return (
    <div className="pb-outlook">
      <div className="pb-outlook-chart"><OutlookChart pricing={pricing} bookedDates={booked} rangeDays={rangeDays} interactive={interactive} /></div>
      {trends && (
        <div className="pb-outlook-trends">
          <TrendBars title="Booking window" unit="days" months={trends.months} values={trends.bookingWindow} highlight={trends.bookingWindowLast365} />
          <TrendBars title="Length of stay" unit="nights" months={trends.months} values={trends.lengthOfStay} highlight={trends.lengthOfStayLast365} />
        </div>
      )}
    </div>
  );
}

// ── Why this price (hub) ───────────────────────────────────────────────────────────────────────────────────────────
export function WhyPriceView({ pricing, state, error }) {
  if (!pricing) return <Unavailable state={state} error={error} what="Pricing data" />;
  const days = pricing.days.filter((day) => day.reason).slice(0, 14);
  return (
    <div className="pb-why">
      <p className="pb-why-intro">How PriceLabs reached each of the next two weeks of prices, one step at a time. Booked nights are dimmed.</p>
      {days.map((day) => {
        const floor = day.reason.thresholds?.min_price?.value;
        const atFloor = Number.isFinite(floor) && Number.isFinite(day.price) && day.price <= floor;
        return (
          <article key={day.date} className={day.booked ? 'booked' : ''}>
            <div className="pb-why-day">
              <strong>{longDate(day.date)}</strong>
              <small>{day.event || (day.booked ? 'Booked' : `${day.demand || '—'} demand`)}</small>
            </div>
            <div className="pb-why-price"><b>{dollars(day.price)}</b><small>{Number.isFinite(day.livePrice) && Math.round(day.livePrice) !== Math.round(day.price) ? `live ${dollars(day.livePrice)}` : ''}</small></div>
            <ol className="pb-why-steps">
              {day.reason.steps.map((step) => (
                <li key={step.key} className={String(step.value).startsWith('-') ? 'down' : String(step.value) === '+0%' ? 'flat' : 'up'}>
                  <span>{step.title}</span><b>{step.value}</b><em>{dollars(step.price)}</em>
                </li>
              ))}
              {atFloor && <li className="floor"><span>Minimum price</span><b>{dollars(floor)}</b></li>}
              {day.reason.override && <li className="override"><span>Override</span><b>active</b></li>}
            </ol>
            <small className="pb-why-min">{day.reason.minStay ? `Min stay ${day.reason.minStay}` : ''}</small>
          </article>
        );
      })}
    </div>
  );
}

// ── Active discounts ───────────────────────────────────────────────────────────────────────────────────────────────
function percentText(value) {
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value)}%`;
}

function AdjustmentStrip({ item, summary }) {
  const applied = useMemo(() => new Set(item.dates), [item]);
  const cells = useMemo(() => {
    const result = [];
    if (!summary.startDate || !summary.endDate) return result;
    const end = parseDay(summary.endDate);
    for (let date = parseDay(summary.startDate); date <= end; date.setDate(date.getDate() + 1)) {
      result.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    }
    return result;
  }, [summary.startDate, summary.endDate]);
  return (
    <div className="pb-strip" style={{ gridTemplateColumns: `repeat(${cells.length || 1}, 1fr)` }} role="img" aria-label={`Nights where ${item.title} applies`}>
      {cells.map((date) => <i key={date} className={applied.has(date) ? 'on' : ''} />)}
    </div>
  );
}

export function AdjustmentsView({ pricing, state, error, compact = false }) {
  if (!pricing) return <Unavailable state={state} error={error} what="Pricing data" />;
  const summary = pricing.adjustments;
  if (!summary) return <Unavailable state="loading" what="Discounts" />;
  const discounts = summary.items.filter((item) => item.direction !== 'premium');
  const premiums = summary.items.filter((item) => item.direction === 'premium');
  const row = (item) => (
    <article className={`pb-adjust ${item.direction}`} key={item.key}>
      <div className="pb-adjust-name">
        <strong>{item.title}</strong>
        <small>{item.isDefault ? 'PriceLabs default' : 'Customization'}</small>
      </div>
      <div className="pb-adjust-strip">
        <AdjustmentStrip item={item} summary={summary} />
        <span><small>{shortDate(summary.startDate)}</small><small>{shortDate(summary.endDate)}</small></span>
      </div>
      <div className="pb-adjust-range">
        <b>{item.minPercent === item.maxPercent ? percentText(item.minPercent) : `${percentText(item.minPercent)} to ${percentText(item.maxPercent)}`}</b>
        <small>avg {percentText(item.averagePercent)}</small>
      </div>
      <div className="pb-adjust-count">
        <b>{item.days}</b>
        <small>of {summary.openDays} open nights{item.nextDate ? ` · next ${shortDate(item.nextDate)}` : ''}</small>
      </div>
    </article>
  );
  return (
    <div className={`pb-adjusts ${compact ? 'compact' : ''}`}>
      <p className="pb-adjust-intro">
        What PriceLabs is applying to your {summary.openDays} open nights{summary.startDate ? `, ${shortDate(summary.startDate)} – ${shortDate(summary.endDate)}` : ''}.
        {!compact && " PriceLabs does not list these settings through its API, so this is read from each night's price explanation."}
      </p>
      {discounts.length ? discounts.map(row) : <div className="pb-empty"><Icon name="info" /><strong>No discounts are being applied to open nights</strong></div>}
      {premiums.length > 0 && <h4 className="pb-adjust-sub">Premiums</h4>}
      {premiums.map(row)}
      {summary.floor?.days > 0 && (
        <p className="pb-adjust-floor">
          <Icon name="warning" />The minimum price of {dollars(summary.floor.price)} is holding {summary.floor.days} {summary.floor.days === 1 ? 'night' : 'nights'}: PriceLabs would price {summary.floor.days === 1 ? 'it' : 'them'} lower.
        </p>
      )}
    </div>
  );
}

// ── Orphan nights ──────────────────────────────────────────────────────────────────────────────────────────────────
const ORPHAN_TONES = ['critical', 'high', 'medium', 'low'];

export function OrphanNights({ nights = [], loading = false }) {
  return (
    <section className="orphan-nights" aria-label="Orphan nights">
      <header>
        <strong>Orphan nights</strong>
        <span>One open night between two stays · discount it, lower the minimum, or extend the earlier stay</span>
      </header>
      {nights.length ? (
        <div className="orphan-list">
          {nights.map((night, index) => (
            <article className={`orphan-card ${ORPHAN_TONES[Math.min(index, ORPHAN_TONES.length - 1)]}`} key={night.date}>
              <div className="orphan-when"><b>{longDate(night.date)}</b><span>{night.daysAway === 0 ? 'Tonight' : night.daysAway === 1 ? 'Tomorrow' : `in ${night.daysAway} days`}</span></div>
              <div className="orphan-rate">{Number.isFinite(night.price) ? centsToMoney(night.price) : '—'}<small>Hospitable rate</small></div>
              <div className="orphan-guests"><span title={night.previousGuest || ''}>{night.previousGuest || 'Guest'}</span><Icon name="arrow_forward" /><span title={night.nextGuest || ''}>{night.nextGuest || 'Guest'}</span></div>
            </article>
          ))}
        </div>
      ) : (
        <div className="orphan-empty">{loading ? 'Checking the calendar…' : 'No orphan nights in the next 60 days'}</div>
      )}
    </section>
  );
}

// ── Hub page ───────────────────────────────────────────────────────────────────────────────────────────────────────
const HUB_TABS = [['kpis', 'KPIs'], ['pace', 'Pace vs market'], ['price', 'Price vs market'], ['discounts', 'Discounts'], ['outlook', 'Market outlook'], ['why', 'Why this price']];

export function PricingPage({ pricingData, kpiData, reservations }) {
  const [tab, setTab] = useState('kpis');
  const [period, setPeriod] = useState('quarter');
  const [compare, setCompare] = useState('prior');
  const [priceRange, setPriceRange] = useState(90);
  const [outlookRange, setOutlookRange] = useState(180);
  const pricing = pricingData.data;
  return (
    <section className="pricing-board hub">
      <div className="pb-hub-bar">
        <SegmentedControl options={HUB_TABS} value={tab} onChange={setTab} label="Pricing page" />
        {tab === 'kpis' && (
          <div className="pb-hub-controls">
            <SegmentedControl options={Object.entries(PERIOD_LABELS)} value={period} onChange={setPeriod} label="Period" />
            <SegmentedControl options={[['prior', 'vs previous period'], ['lastYear', 'vs last year']]} value={compare} onChange={setCompare} label="Compare with" />
          </div>
        )}
        {tab === 'price' && <SegmentedControl options={[[30, '30 days'], [90, '90 days'], [180, '180 days']]} value={priceRange} onChange={setPriceRange} label="Range" />}
        {tab === 'outlook' && <SegmentedControl options={[[90, '90 days'], [180, '180 days'], [360, '1 year']]} value={outlookRange} onChange={setOutlookRange} label="Range" />}
      </div>
      {tab !== 'kpis' && <SyncStrip pricing={pricing} />}
      <div className={`pb-hub-body ${tab}`}>
        {tab === 'kpis' && <KpiView kpis={kpiData.data} state={kpiData.state} error={kpiData.error} period={period} compare={compare} />}
        {tab === 'pace' && <PaceView pricing={pricing} state={pricingData.state} error={pricingData.error} />}
        {tab === 'price' && <PriceView pricing={pricing} state={pricingData.state} error={pricingData.error} reservations={reservations} rangeDays={priceRange} interactive showPosition />}
        {tab === 'discounts' && <AdjustmentsView pricing={pricing} state={pricingData.state} error={pricingData.error} />}
        {tab === 'outlook' && <OutlookView pricing={pricing} state={pricingData.state} error={pricingData.error} reservations={reservations} rangeDays={outlookRange} interactive />}
        {tab === 'why' && <WhyPriceView pricing={pricing} state={pricingData.state} error={pricingData.error} />}
      </div>
    </section>
  );
}

// ── Wallboard slides ───────────────────────────────────────────────────────────────────────────────────────────────
const SLIDE_TITLES = {
  kpis: ['Performance', 'Key numbers, to date'],
  pace: ['Pace vs market', 'Your occupancy against the market'],
  price: ['Price vs market', 'Next 90 days'],
  discounts: ['Active discounts', 'What PriceLabs is applying to open nights'],
  outlook: ['Market outlook', 'Occupancy, booking window and length of stay'],
};

export function TvPricingSlide({ slide, pricingData, kpiData, reservations, period, setPeriod }) {
  const [title, subtitle] = SLIDE_TITLES[slide];
  const pricing = pricingData.data;
  return (
    <section className={`pricing-board tv slide-${slide}`}>
      <header className="pb-tv-head">
        <div><strong>{title}</strong><span>{subtitle}</span></div>
        {slide === 'kpis' && <SegmentedControl options={Object.entries(PERIOD_LABELS)} value={period} onChange={setPeriod} label="Period" />}
        {slide !== 'kpis' && <SyncStrip pricing={pricing} />}
      </header>
      <div className="pb-tv-body">
        {slide === 'kpis' && <KpiView kpis={kpiData.data} state={kpiData.state} error={kpiData.error} period={period} compare={period === 'year' ? 'lastYear' : 'prior'} compact />}
        {slide === 'pace' && <PaceView pricing={pricing} state={pricingData.state} error={pricingData.error} compact />}
        {slide === 'price' && <PriceView pricing={pricing} state={pricingData.state} error={pricingData.error} reservations={reservations} rangeDays={90} interactive={false} />}
        {slide === 'discounts' && <AdjustmentsView pricing={pricing} state={pricingData.state} error={pricingData.error} compact />}
        {slide === 'outlook' && <OutlookView pricing={pricing} state={pricingData.state} error={pricingData.error} reservations={reservations} rangeDays={180} interactive={false} />}
      </div>
    </section>
  );
}
